import { useEffect, useMemo, useState } from 'react'

import {
  advanceBracket,
  deleteBracket,
  setBracketMatch,
} from '../lib/data.js'
import { parsFor } from '../lib/scoring.js'
import { lastNames } from '../lib/teamName.js'
import TvBracketGrid from '../leaderboard/TvBracketGrid.jsx'

const TBD = '— TBD —'

function teamName(flight, teamId) {
  if (!teamId) return TBD
  return lastNames(flight?.teams?.[teamId]?.info?.name) || teamId
}

// Group rounds by day for column rendering.
function roundsByDay(rounds) {
  const grouped = {}
  for (const [key, r] of Object.entries(rounds || {})) {
    const day = r.day || 1
    if (!grouped[day]) grouped[day] = []
    grouped[day].push({ key, ...r })
  }
  for (const d of Object.keys(grouped)) {
    grouped[d].sort((a, b) => (a.order || 0) - (b.order || 0))
  }
  return grouped
}

export default function BracketTab({ tournamentId, tournament, flights, brackets, tournamentInfo }) {
  const bracketDays = Number(tournamentInfo?.bracketDays) || Number(tournamentInfo?.tournamentDays) || 1
  const sortedFlights = flights.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const [fullscreen, setFullscreen] = useState(false)
  const pars = parsFor(tournament || { info: tournamentInfo })

  // Brackets are auto-built at the tournament level (TournamentDetail's
  // BracketAutoGenerate) the moment the tournament opens, regardless of tab —
  // so this tab, the TV Monitor, and Full Screen all show the whole field
  // without needing to be visited first.

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])

  if (sortedFlights.length === 0) {
    return <div className="card text-center py-10 text-gray-500">No flights yet — generate flights first.</div>
  }

  const builtCount = sortedFlights.filter((f) => brackets?.[f.id]).length

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Brackets — All Flights</h2>
          <p className="text-sm text-gray-500">
            {bracketDays}-day brackets. Top 8 teams seed Round 1 in 1↔8, 2↔7, 3↔6, 4↔5 order.
            Brackets build automatically — {builtCount}/{sortedFlights.length} ready.
          </p>
        </div>
        <button
          className="btn-secondary whitespace-nowrap"
          onClick={() => setFullscreen(true)}
          title="Show every flight's bracket full-screen, same as the TV monitor">
          ⛶ Full Screen (TV)
        </button>
      </div>

      {sortedFlights.map((f) => (
        <FlightBracketSection
          key={f.id}
          tournamentId={tournamentId}
          flight={f}
          bracket={brackets?.[f.id] || null}
          pars={pars}
        />
      ))}

      {fullscreen ? (
        <div className="fixed inset-0 z-50">
          <TvBracketGrid tournamentIdProp={tournamentId} />
          <button
            onClick={() => setFullscreen(false)}
            className="fixed top-4 right-4 z-[60] rounded-lg w-10 h-10 flex items-center justify-center text-2xl font-bold text-white"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid var(--ge-primary)' }}
            title="Exit full-screen (Esc)">
            ×
          </button>
        </div>
      ) : null}
    </div>
  )
}

// One flight's bracket, stacked on the page so the admin scrolls through every
// flight instead of picking one from a dropdown. Editable board + visual, with
// per-flight Advance / Reset. Advancement also runs automatically from scores
// (the tournament-level watcher in TournamentDetail), so this is mostly review.
function FlightBracketSection({ tournamentId, flight, bracket, pars }) {
  const [notice, setNotice] = useState(null)
  useEffect(() => {
    if (!notice) return
    const id = setTimeout(() => setNotice(null), 4000)
    return () => clearTimeout(id)
  }, [notice])

  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <h3 className="text-base font-bold">{flight.info?.name || flight.id}</h3>
        {bracket ? (
          <div className="flex gap-2 items-center">
            <button
              className="btn-secondary text-xs"
              onClick={async () => {
                const filled = await advanceBracket(tournamentId, flight.id)
                if (filled === 0) alert('Nothing to advance — winners not decided on the latest day yet.')
              }}
              title="Read winners and propagate to the next day's matchups">
              ▶ Advance Winners
            </button>
            <button
              className="text-xs text-red-600 font-bold"
              onClick={() => {
                if (!confirm(`Reset ${flight.info?.name || flight.id}'s bracket? It will rebuild fresh.`)) return
                deleteBracket(tournamentId, flight.id)
              }}>
              Reset
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-400">Building bracket…</span>
        )}
      </div>

      {bracket ? (
        <>
          <div className="flex flex-col xl:flex-row gap-4">
            <div className="xl:flex-shrink-0">
              <BracketBoard tournamentId={tournamentId} flight={flight} bracket={bracket} />
            </div>
            <div className="flex-1 min-w-0">
              <VisualBracket flight={flight} bracket={bracket} />
              <FinalStandings flight={flight} bracket={bracket} />
            </div>
          </div>
          <BracketAutoAdvance
            tournamentId={tournamentId}
            flight={flight}
            bracket={bracket}
            pars={pars}
            onNotice={setNotice}
          />
          {notice ? (
            <div className="text-xs font-bold px-3 py-2 rounded-lg"
                 style={{ backgroundColor: 'rgba(var(--ge-primary-rgb),0.10)', color: 'var(--ge-primary)' }}>
              {notice}
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-center py-6 text-gray-400 text-sm">Building bracket…</div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Final Standings — derives 1st through 8th place from the four Day 3
// match results. Each match decides two positions; we read winner/loser
// off champ/third/consf/fifth in order. Renders cleanly for screen and
// print (PBCC dark green / gold, 1st place highlighted).
// ────────────────────────────────────────────────────────────────────────
function FinalStandings({ flight, bracket }) {
  const rounds = bracket?.rounds || {}
  const order = [
    { round: 'champ', place: 1, slot: 'winner', label: '1st Place' },
    { round: 'champ', place: 2, slot: 'loser',  label: '2nd Place' },
    { round: 'third', place: 3, slot: 'winner', label: '3rd Place' },
    { round: 'third', place: 4, slot: 'loser',  label: '4th Place' },
    { round: 'consf', place: 5, slot: 'winner', label: '5th Place' },
    { round: 'consf', place: 6, slot: 'loser',  label: '6th Place' },
    { round: 'fifth', place: 7, slot: 'winner', label: '7th Place' },
    { round: 'fifth', place: 8, slot: 'loser',  label: '8th Place' },
  ]

  const lookupTeam = (roundKey, slot) => {
    const m = Object.values(rounds[roundKey]?.matches || {})[0]
    if (!m) return null
    if (slot === 'winner') return m.winnerId || null
    if (m.winnerId === m.a) return m.b || null
    if (m.winnerId === m.b) return m.a || null
    return null
  }

  const standings = order.map((row) => ({
    ...row,
    teamId: lookupTeam(row.round, row.slot),
  }))
  // Don't render the panel until at least one place is decided.
  if (!standings.some((s) => s.teamId)) return null

  return (
    <div className="rounded-2xl p-5 mt-4" style={{ backgroundColor: 'var(--ge-primary)', border: '1px solid var(--ge-primary)' }}>
      <p className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: 'var(--ge-primary)' }}>
        Final Standings — {flight.info?.name || flight.id}
      </p>
      <ol className="space-y-1.5">
        {standings.map((row) => {
          const name = row.teamId ? (lastNames(flight.teams?.[row.teamId]?.info?.name) || row.teamId) : '—'
          const isFirst = row.place === 1
          return (
            <li
              key={row.place}
              className="flex justify-between items-baseline gap-3 px-3 py-1.5 rounded"
              style={{
                backgroundColor: isFirst ? 'rgba(var(--ge-primary-rgb),0.15)' : 'transparent',
                border: isFirst ? '1px solid rgba(var(--ge-primary-rgb),0.5)' : '1px solid transparent',
              }}>
              <span
                className="font-extrabold text-sm whitespace-nowrap"
                style={{ color: isFirst ? 'var(--ge-primary)' : '#FFFFFF' }}>
                {isFirst ? '🏆 ' : ''}{row.label}
              </span>
              <span
                className="text-sm font-bold truncate text-right"
                style={{ color: isFirst ? 'var(--ge-primary)' : 'rgba(255,255,255,0.92)' }}>
                {name}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Auto-advance — when both teams in a match have all 18 holes scored,
// determine the match-play winner (whoever won more holes) and write it.
// When all 4 r1 winners are decided, propagate to Day 2; same for Day 2 → Day 3.
// Admin can still override anything manually — auto-fill only writes when
// `winnerId` is currently null/undefined.
// ────────────────────────────────────────────────────────────────────────
export function BracketAutoAdvance({ tournamentId, flight, bracket, pars, onNotice }) {
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const rounds = bracket?.rounds || {}
      const tasks = []
      for (const [roundKey, round] of Object.entries(rounds)) {
        for (const [matchId, m] of Object.entries(round.matches || {})) {
          if (m.winnerId || !m.a || !m.b) continue
          const winnerId = matchPlayWinner(m.a, m.b, flight, pars)
          if (winnerId) {
            tasks.push(
              setBracketMatch(tournamentId, flight.id, roundKey, matchId, { winnerId })
                .then(() => ({ roundKey, matchId, winnerId })),
            )
          }
        }
      }
      if (tasks.length === 0) return
      const decided = await Promise.all(tasks)
      if (cancelled) return
      // After winners are written, propagate to the next day. We only emit
      // an "auto-generated" notification when at least one whole day's
      // worth of matches just became decided.
      const filled = await advanceBracket(tournamentId, flight.id)
      if (cancelled) return
      const r1Decided = decided.filter((d) => d.roundKey === 'r1').length
      const day2Decided = decided.filter((d) => ['wb2', 'cb2'].includes(d.roundKey)).length
      if (r1Decided === 4 && filled > 0) {
        onNotice('Day 2 brackets generated automatically — all Day 1 matches complete')
      } else if (day2Decided === 4 && filled > 0) {
        onNotice('Day 3 brackets generated automatically — all Day 2 matches complete')
      }
    }
    run()
    return () => { cancelled = true }
  }, [bracket, flight, tournamentId, pars, onNotice])
  return null
}

// Match-play winner: count holes won by each team across all 18 holes.
// Returns null when scores are incomplete or the match is tied.
function matchPlayWinner(teamAId, teamBId, flight, pars) {
  const a = flight?.teams?.[teamAId]
  const b = flight?.teams?.[teamBId]
  if (!a || !b) return null
  const sa = a.scores || {}
  const sb = b.scores || {}
  // Count every hole both teams scored — regulation 1-18 plus any sudden-death
  // playoff holes (19+). Hole scores are stored as { …, bestBall, updatedAt };
  // bestBall is the match-play number (reading the object gave NaN, which is
  // why this never decided a winner before).
  let aWon = 0
  let bWon = 0
  let played = 0
  const holes = new Set()
  for (const k of Object.keys(sa)) { const n = Number(k); if (Number.isFinite(n) && n >= 1) holes.add(n) }
  for (const k of Object.keys(sb)) { const n = Number(k); if (Number.isFinite(n) && n >= 1) holes.add(n) }
  for (const h of [...holes].sort((x, y) => x - y)) {
    const va = Number(sa[h]?.bestBall)
    const vb = Number(sb[h]?.bestBall)
    if (!Number.isFinite(va) || !Number.isFinite(vb)) continue
    played++
    if (va < vb) aWon++
    else if (vb < va) bWon++
  }
  // Decided on the match-play clinch rule — up by more holes than remain (e.g.
  // 3&2, before holes 17-18 are played) or ahead once all holes are in. Same
  // rule the scorecard uses. A tie is NOT decided — sudden death continues.
  const lead = Math.abs(aWon - bWon)
  const remaining = Math.max(0, 18 - played)
  if (lead > 0 && lead > remaining) return aWon > bWon ? teamAId : teamBId
  return null
}

function BracketBoard({ tournamentId, flight, bracket }) {
  const grouped = useMemo(() => roundsByDay(bracket.rounds), [bracket])
  const days = Object.keys(grouped).map(Number).sort((a, b) => a - b)
  const allTeams = Object.entries(flight.teams || {}).map(([id, t]) => ({ id, name: lastNames(t.info?.name) || id }))

  return (
    <div className="card overflow-x-auto">
      <div className="flex gap-6 min-w-max">
        {days.map((d) => (
          <div key={d} className="flex flex-col gap-3 min-w-[260px]">
            <h3 className="text-sm uppercase tracking-widest font-bold text-green-deep border-b pb-1">
              Day {d}
            </h3>
            {grouped[d].map((round) => (
              <div key={round.key}>
                <p className="text-xs font-bold text-gray-600 mb-1">{round.label}</p>
                <div className="flex flex-col gap-2">
                  {Object.entries(round.matches || {}).map(([mid, m]) => (
                    <MatchBox
                      key={mid}
                      tournamentId={tournamentId}
                      flightId={flight.id}
                      flight={flight}
                      roundKey={round.key}
                      matchId={mid}
                      match={m}
                      allTeams={allTeams}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// Final-position labels — every Day-3 match decides two places, so each
// entry carries the winner and loser label for that match.
const FINAL_LABELS = {
  f:     { winner: '🏆 CHAMPION', loser: '2ND PLACE' },
  champ: { winner: '🏆 CHAMPION', loser: '2ND PLACE' },
  third: { winner: '3RD PLACE',   loser: '4TH PLACE' },
  consf: { winner: '5TH PLACE',   loser: '6TH PLACE' },
  fifth: { winner: '7TH PLACE',   loser: '8TH PLACE' },
}

function MatchBox({ tournamentId, flightId, flight, roundKey, matchId, match, allTeams }) {
  const aName = teamName(flight, match.a)
  const bName = teamName(flight, match.b)
  const winnerIs = (id) => match.winnerId && match.winnerId === id
  const finalLabels = FINAL_LABELS[roundKey]
  const winnerName = match.winnerId
    ? (lastNames(flight.teams?.[match.winnerId]?.info?.name) || match.winnerId)
    : null
  const loserId = match.winnerId === match.a
    ? match.b
    : match.winnerId === match.b ? match.a : null
  const loserName = loserId ? (lastNames(flight.teams?.[loserId]?.info?.name) || loserId) : null

  const setTeam = (side, val) => {
    setBracketMatch(tournamentId, flightId, roundKey, matchId, { [side]: val || null })
  }
  const setWinner = (id) => {
    setBracketMatch(tournamentId, flightId, roundKey, matchId, { winnerId: id || null })
  }

  return (
    <div>
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <Slot
          side="a"
          label={aName}
          winner={winnerIs(match.a)}
          teamId={match.a}
          allTeams={allTeams}
          onPick={(val) => setTeam('a', val)}
          onWin={() => setWinner(match.a)}
        />
        <div className="border-t" />
        <Slot
          side="b"
          label={bName}
          winner={winnerIs(match.b)}
          teamId={match.b}
          allTeams={allTeams}
          onPick={(val) => setTeam('b', val)}
          onWin={() => setWinner(match.b)}
        />
      </div>
      {finalLabels && winnerName ? (
        <div className="mt-1 text-center space-y-0.5">
          <div className="text-[10px] font-extrabold uppercase tracking-widest"
               style={{ color: 'var(--ge-primary)' }}>
            {finalLabels.winner} · {winnerName}
          </div>
          {loserName ? (
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
              {finalLabels.loser} · {loserName}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Visual bracket — SVG diagram with connector lines, mirrors the same data
// the list view edits. Lights up gold for winners' path, grey for losers'.
// ────────────────────────────────────────────────────────────────────────

const PBCC_GREEN = 'var(--ge-primary)'
const PBCC_GOLD = 'var(--ge-primary)'

export function VisualBracket({ flight, bracket, fullscreen = false, tv = false }) {
  const rounds = bracket?.rounds || {}
  const r1 = rounds.r1
  if (!r1) {
    return (
      <div className="rounded-2xl p-6 text-sm text-white/70 text-center"
           style={{ backgroundColor: PBCC_GREEN }}>
        Generate the bracket to see the visual board.
      </div>
    )
  }

  // Layout constants — tuned so the 3-day demo bracket fits comfortably in the
  // remaining horizontal space on a desktop (~700–900 px). `tv` mode makes the
  // boxes chunkier and the text bigger for the TV bracket grid (each tile is
  // ~1/12 of the screen, so text needs to be larger relative to the boxes).
  const BOX_W = tv ? 230 : 170
  const BOX_H = tv ? 62 : 44
  const COL_GAP = tv ? 22 : 60
  const MATCH_GAP = tv ? 44 : 36
  // Font sizes (SVG units). tv bumps text up relative to the boxes so it reads
  // large when the whole board is scaled down into a grid tile.
  const FS = tv
    ? { day: 14, round: 14, team: 22, finWin: 18, finLose: 15, labW: 22, labL: 42 }
    : { day: 11, round: 9,  team: 11, finWin: 10, finLose: 9,  labW: 14, labL: 28 }
  const TRUNC = tv ? 18 : 20
  const colX = (i) => i * (BOX_W + COL_GAP)
  const centerOf = (m) => m.y + BOX_H / 2

  // Day 1 — Round 1 (4 matches stacked).
  const layoutR1 = Object.entries(r1.matches || {}).map(([mid, m], i) => ({
    key: `r1-${mid}`, mid, m, label: 'Round 1', round: 'r1',
    x: colX(0), y: i * (BOX_H + MATCH_GAP),
  }))
  const r1Bottom = layoutR1.length > 0
    ? layoutR1[layoutR1.length - 1].y + BOX_H
    : 0

  // Day 1 — Semifinals (1-day single-elim only).
  const layoutSF = Object.entries(rounds.sf?.matches || {}).map(([mid, m], i) => {
    const src1 = layoutR1[i * 2]
    const src2 = layoutR1[i * 2 + 1]
    const y = src1 && src2 ? (centerOf(src1) + centerOf(src2)) / 2 - BOX_H / 2 : i * (BOX_H + MATCH_GAP)
    return { key: `sf-${mid}`, mid, m, label: 'Semifinals', round: 'sf', sources: [src1, src2], x: colX(1), y }
  })

  // Day 1 — Final (1-day only).
  const layoutF = Object.entries(rounds.f?.matches || {}).map(([mid, m]) => {
    const [s1, s2] = layoutSF
    const y = s1 && s2 ? (centerOf(s1) + centerOf(s2)) / 2 - BOX_H / 2 : 0
    return { key: `f-${mid}`, mid, m, label: 'Final', round: 'f', sources: [s1, s2], x: colX(2), y }
  })

  // Day 2 — Winners Bracket (paired between consecutive R1 matches).
  const layoutWB2 = Object.entries(rounds.wb2?.matches || {}).map(([mid, m], i) => {
    const src1 = layoutR1[i * 2]
    const src2 = layoutR1[i * 2 + 1]
    const y = src1 && src2 ? (centerOf(src1) + centerOf(src2)) / 2 - BOX_H / 2 : i * (BOX_H + MATCH_GAP)
    return { key: `wb2-${mid}`, mid, m, label: rounds.wb2.label, round: 'wb2', sources: [src1, src2], x: colX(1), y }
  })

  // Consolation column starts a section below the winners' R1.
  const cbSectionStart = r1Bottom + (tv ? 40 : 60)
  const layoutCB2 = Object.entries(rounds.cb2?.matches || {}).map(([mid, m], i) => {
    // Consolation pair sources are the LOSERS of the same R1 matches the
    // winners feed into wb2 — visually the connector from the R1 match's
    // bottom slot heads down here.
    const src1 = layoutR1[i * 2]
    const src2 = layoutR1[i * 2 + 1]
    return {
      key: `cb2-${mid}`, mid, m, label: rounds.cb2.label, round: 'cb2',
      sources: [src1, src2], consolationLine: true,
      x: colX(1), y: cbSectionStart + i * (BOX_H + MATCH_GAP),
    }
  })

  // Day 3 (or Day 4) finals — championship + 3rd place + consolation final
  // + 5th place. Stacked top-to-bottom in the rightmost column with
  // generous, even spacing. Each match owns 130 px of vertical pitch:
  // round label above (~14 px), 44 px box, winner label (~14 px), loser
  // label (~14 px), plus ~44 px of breathing room before the next match.
  const finalCol = bracket.bracketDays === 1 ? 0 /* unused */ : 2
  const D3_PITCH = tv ? 150 : 130
  const D3_TOP = 20 // small top inset so the first label has room
  const finalSpec = [
    { round: 'champ', label: 'Championship',      sources: layoutWB2, consolation: false },
    { round: 'third', label: '3rd Place',         sources: layoutWB2, consolation: true  },
    { round: 'consf', label: 'Consolation Final', sources: layoutCB2, consolation: false },
    { round: 'fifth', label: '5th Place',         sources: layoutCB2, consolation: true  },
  ]
  const layoutFinals = []
  finalSpec.forEach((spec, i) => {
    const matchEntry = Object.entries(rounds[spec.round]?.matches || {})[0]
    if (!matchEntry) return
    const [mid, m] = matchEntry
    layoutFinals.push({
      key: `${spec.round}-${mid}`, mid, m,
      label: spec.label, round: spec.round,
      sources: spec.sources,
      ...(spec.consolation ? { consolationLine: true } : {}),
      x: colX(finalCol),
      y: D3_TOP + i * D3_PITCH,
    })
  })

  const all = [...layoutR1, ...layoutSF, ...layoutF, ...layoutWB2, ...layoutCB2, ...layoutFinals]

  // SVG bounds. Final-round boxes carry two label lines below the box
  // (winner + loser), so add 36 px when measuring against those rows.
  const totalCols = bracket.bracketDays >= 3 ? 3 : (bracket.bracketDays === 2 ? 2 : 3)
  const MARGIN = tv ? 8 : 30
  const HEAD = tv ? 0 : 24 // tv: no internal day band — day labels live in the tile header row
  const totalWidth = colX(totalCols) + MARGIN
  const totalHeight = Math.max(
    ...all.map((m) => m.y + BOX_H + (FINAL_LABELS[m.round] ? (tv ? 58 : 36) : 0)),
    200,
  ) + MARGIN

  // Day labels above each column.
  const dayLabel = (col) => {
    if (bracket.bracketDays === 1) return ['Day 1 · R1', 'Semifinals', 'Final'][col]
    if (bracket.bracketDays === 2) return ['Day 1', 'Day 2', ''][col]
    return ['Day 1', 'Day 2', `Day ${bracket.bracketDays}`][col]
  }

  // In fullscreen mode the SVG scales to fill the parent container width
  // (preserving aspect ratio); otherwise it renders at its natural size.
  const svgProps = fullscreen
    ? {
        width: '100%', height: '100%',
        viewBox: `0 0 ${totalWidth} ${totalHeight + HEAD}`,
        // tv: stretch to fill the whole tile (full width AND height, no
        // letterbox). Elsewhere: preserve aspect ratio and cap height.
        preserveAspectRatio: tv ? 'none' : 'xMidYMid meet',
        style: tv ? { display: 'block' } : { display: 'block', maxHeight: 'calc(100vh - 100px)' },
      }
    : { width: totalWidth, height: totalHeight + HEAD, style: { display: 'block' } }

  return (
    <div className={fullscreen ? '' : 'rounded-2xl p-4 overflow-x-auto'}
         style={fullscreen ? { width: '100%', height: '100%' } : { backgroundColor: PBCC_GREEN }}>
      {!fullscreen ? (
        <p className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: PBCC_GOLD }}>
          Visual Bracket
        </p>
      ) : null}
      <svg {...svgProps}>
        {/* Day headers — hidden in tv mode (shown in the tile's header row) */}
        {!tv && [0, 1, 2].slice(0, totalCols).map((c) => (
          <text key={c} x={colX(c) + BOX_W / 2} y={14}
                textAnchor="middle"
                fontSize={FS.day} fontWeight="700"
                fill={PBCC_GOLD}
                style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {dayLabel(c)}
          </text>
        ))}
        <g transform={`translate(0, ${HEAD})`}>
          {/* Connector lines intentionally omitted — names already advance into
              the correct day columns, so the tie-in lines only confused people. */}
          {/* Match boxes */}
          {all.map((entry) => (
            <MatchSVG key={entry.key} flight={flight} entry={entry} BOX_W={BOX_W} BOX_H={BOX_H} FS={FS} TRUNC={TRUNC} />
          ))}
        </g>
      </svg>
    </div>
  )
}

function MatchSVG({ flight, entry, BOX_W, BOX_H, FS = { round: 9, team: 11, finWin: 10, finLose: 9, labW: 14, labL: 28 }, TRUNC = 20 }) {
  const { x, y, m, label, round } = entry
  const aName = teamName(flight, m.a)
  const bName = teamName(flight, m.b)
  const winnerIsA = m.winnerId && m.winnerId === m.a
  const winnerIsB = m.winnerId && m.winnerId === m.b
  const slotH = BOX_H / 2
  const finalLabels = FINAL_LABELS[round]
  const winnerName = m.winnerId ? teamName(flight, m.winnerId) : null
  const loserId = m.winnerId === m.a ? m.b : m.winnerId === m.b ? m.a : null
  const loserName = loserId ? teamName(flight, loserId) : null

  const slotFill = (isWinner, hasTeam) => {
    if (isWinner) return PBCC_GOLD
    if (hasTeam) return 'rgba(255,255,255,0.06)'
    return 'rgba(255,255,255,0.03)'
  }
  const slotText = (isWinner) => (isWinner ? PBCC_GREEN : '#FFFFFF')
  const truncate = (s) => (s.length > TRUNC ? s.slice(0, TRUNC - 2) + '…' : s)

  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Round label above the box */}
      <text x={0} y={-4} fontSize={FS.round} fontWeight="700" fill="rgba(255,255,255,0.55)"
            style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        {label}
      </text>
      {/* Top slot */}
      <rect x={0} y={0} width={BOX_W} height={slotH}
            fill={slotFill(winnerIsA, !!m.a)}
            stroke="rgba(var(--ge-primary-rgb),0.45)" strokeWidth="1" />
      <text x={8} y={slotH / 2 + 4} fontSize={FS.team} fontWeight={winnerIsA ? 700 : 500} fill={slotText(winnerIsA)}>
        {truncate(aName)}
      </text>
      {/* Bottom slot */}
      <rect x={0} y={slotH} width={BOX_W} height={slotH}
            fill={slotFill(winnerIsB, !!m.b)}
            stroke="rgba(var(--ge-primary-rgb),0.45)" strokeWidth="1" />
      <text x={8} y={slotH + slotH / 2 + 4} fontSize={FS.team} fontWeight={winnerIsB ? 700 : 500} fill={slotText(winnerIsB)}>
        {truncate(bName)}
      </text>
      {/* Final-position labels — winner in gold, loser in light grey */}
      {finalLabels && winnerName ? (
        <text x={BOX_W / 2} y={BOX_H + FS.labW} textAnchor="middle"
              fontSize={FS.finWin} fontWeight="800" fill={PBCC_GOLD}
              style={{ letterSpacing: '0.1em' }}>
          {finalLabels.winner} · {truncate(winnerName)}
        </text>
      ) : null}
      {finalLabels && loserName ? (
        <text x={BOX_W / 2} y={BOX_H + FS.labL} textAnchor="middle"
              fontSize={FS.finLose} fontWeight="700" fill="rgba(255,255,255,0.65)"
              style={{ letterSpacing: '0.1em' }}>
          {finalLabels.loser} · {truncate(loserName)}
        </text>
      ) : null}
    </g>
  )
}

function Slot({ label, winner, teamId, allTeams, onPick, onWin }) {
  const [editing, setEditing] = useState(false)
  return (
    <div
      className={`flex justify-between items-center px-2 py-1.5 text-sm ${
        winner ? 'bg-green-mint font-bold text-green-deep' : ''
      }`}>
      {editing ? (
        <select
          autoFocus
          className="input py-1 text-xs flex-1 mr-2"
          value={teamId || ''}
          onChange={(e) => { onPick(e.target.value); setEditing(false) }}
          onBlur={() => setEditing(false)}>
          <option value="">— TBD —</option>
          {allTeams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      ) : (
        <button
          className="flex-1 text-left truncate hover:underline"
          onClick={() => setEditing(true)}>
          {label}
        </button>
      )}
      <button
        className={`shrink-0 ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
          winner
            ? 'bg-green-deep text-white'
            : teamId ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'opacity-30'
        }`}
        disabled={!teamId}
        onClick={onWin}
        title={winner ? 'Winner' : 'Set as winner'}>
        {winner ? '✓ Win' : 'Win'}
      </button>
    </div>
  )
}
